import React, { CSSProperties, ReactElement } from "react";
import {
  ToastContainer as RTToastContainer,
  toast as RTtoast,
  Slide,
  ToastPosition,
  ToastContent,
  UpdateOptions,
  Id as ToastId,
} from "react-toastify";
import type { CSSPropertiesWithMultiValues } from "@emotion/serialize";
import { ToastStyle } from "./Toast.styles";

export type ToastProps = {
  /** Unique identifier for your toast */
  containerId: string;
  /** Custom styles for the container of the Toast */
  containerStyles?: CSSProperties;
  /** Custom styles for the toast content */
  toastStyles?: CSSPropertiesWithMultiValues;
  /** One of top-right, top-center, top-left, bottom-right, bottom-center, bottom-left */
  position?: ToastPosition;
  /** Delay in ms to close the toast. If set to false, the notification needs to be closed manually */
  autoClose?: false | number;
  /** A React Component to replace the default close button or false to hide the button */
  closeButton?:
    | ReactElement
    | ((props: unknown) => React.ReactElement)
    | boolean;
  /** Keep the timer running or not on hover */
  pauseOnHover?: boolean;
  /** Dismiss toast on click */
  closeOnClick?: boolean;
};

type ToastOptions = Omit<ToastProps, "containerStyles" | "toastStyles"> & {
  toastId?: string | number;
};
type ToastUpdateOptions = UpdateOptions & {
  containerId: string;
};

export const ToastContainer: React.FC<ToastProps> = ({
  containerId,
  containerStyles,
  toastStyles,
  position = "top-right",
  autoClose = 5000,
  closeButton = false,
  pauseOnHover = true,
  closeOnClick = false,
}) => (
  <ToastStyle toastStyles={toastStyles}>
    <RTToastContainer
      style={containerStyles}
      hideProgressBar
      transition={Slide}
      draggable={false}
      enableMultiContainer={true}
      containerId={containerId}
      position={position}
      autoClose={autoClose}
      closeButton={closeButton}
      pauseOnHover={pauseOnHover}
      closeOnClick={closeOnClick}
      pauseOnFocusLoss={false}
    />
  </ToastStyle>
);

export const toast = (content: ToastContent, options?: ToastOptions) =>
  RTtoast(content, options);

toast.update = (id: ToastId, options?: ToastUpdateOptions) =>
  RTtoast.update(id, options);

ToastContainer.displayName = "ToastContainer";

export default ToastContainer;
